/**
 * Страница сообщений — топовый современный мессенджер
 * Дата: 30.01.2026 — REDESIGN: вау-эффект с микроанимациями
 * Дата: 31.01.2026 — Добавлена модалка инициирования диалога (TODO-051)
 * 
 * СВЯЗАННЫЕ МОДУЛИ:
 * - ConversationList.tsx - список бесед слева
 * - ChatWindow.tsx - окно чата справа
 * - MessageBubble.tsx - bubble сообщений
 * - WebSocketContext - realtime обновления
 * - InitiateConversationModal.tsx - модалка инициирования диалога (31.01.2026)
 * 
 * ОСОБЕННОСТИ ДИЗАЙНА:
 * - Glassmorphism для sidebar header
 * - Современный поиск с иконкой и анимацией
 * - Градиентный пустой state
 * - 8pt сетка для отступов
 * - Кнопка "Начать диалог" с градиентным фоном (31.01.2026)
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { useMessages } from '@/hooks/useMessages';
import { ConversationList } from '@/components/features/messages/ConversationList';
import { ChatWindow } from '@/components/features/messages/ChatWindow';
import { ChannelStatusMonitor } from '@/components/features/messages/ChannelStatusMonitor';
import { NewMessageToast } from '@/components/features/messages/NewMessageToast';
import { InitiateConversationModal } from '@/components/features/messages/InitiateConversationModal';
import { Spinner, TenantSelector, ChannelFilterTabs, type ChannelFilter } from '@/components/ui';
import { MainLayout } from '@/components/layout/MainLayout';
import { useTenantScope } from '@/hooks/useTenantScope';
import { WebSocketProvider, useWebSocketContext } from '@/contexts/WebSocketContext';
import { messagesApi } from '@/api/messages';
import { MessageSquare, Search, Wifi, WifiOff, X, AlertCircle, Plus, Users, User, SlidersHorizontal, HelpCircle } from 'lucide-react';
import { PageGuide, HelpTooltip } from '@/components/ui/help';
import { messagesHelp } from '@/content/help';

const getConversationDisplayName = (conversation?: {
  patient_name?: string;
  chat_title?: string;
  chat_type?: string;
  phone_normalized?: string;
  phone?: string;
} | null): string | undefined => {
  if (!conversation) return undefined;

  const messengerFallback = conversation.phone_normalized || conversation.phone;
  if (conversation.chat_type === 'group') {
    return conversation.chat_title || messengerFallback || conversation.patient_name;
  }

  return conversation.patient_name || conversation.chat_title || messengerFallback;
};

const isCanonicalPhone = (value?: string | null): boolean => {
  return !!value && /^7\d{10}$/.test(value);
};

const getConversationThreadKey = (conversation?: {
  phone_normalized?: string;
  phone?: string;
  messenger?: string;
} | null): string => {
  if (!conversation) return '';

  const raw = conversation.phone_normalized || conversation.phone || '';
  if (!raw || raw.startsWith('tg:') || raw.startsWith('max:') || isCanonicalPhone(raw)) {
    return raw;
  }

  if (conversation.messenger?.includes('telegram')) {
    return `tg:${raw}`;
  }
  if (conversation.messenger === 'max') {
    return `max:${raw}`;
  }

  return raw;
};

export type InboxScope = 'all' | 'unread';

export function filterLoadedConversations<T extends { unread_count: number }>(
  items: T[],
  scope: InboxScope,
): T[] {
  if (scope === 'unread') return items.filter((item) => item.unread_count > 0);
  return items;
}

const channelCountFilters: ChannelFilter[] = [
  'waba',
  'regular',
  'telegram_bot',
  'telegram_client',
  'max',
  'max_client',
  'sms',
];

export function MessagesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [selectedPatientName, setSelectedPatientName] = useState<string | undefined>(undefined);
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('all');
  // Дата: 12.02.2026 - Фильтр по типу чата: все/личные/групповые
  const [chatTypeFilter, setChatTypeFilter] = useState<'all' | 'personal' | 'group'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Дата: 31.01.2026 — Флаг: чата с пациентом нет (для перехода из /appointments)
  const [noConversationForPhone, setNoConversationForPhone] = useState<string | null>(null);
  // Дата: 07.02.2026 — Флаг: проверка через API уже выполнена для текущего телефона
  const checkedPhoneRef = useRef<string | null>(null);
  // Дата: 31.01.2026 — Модалка инициирования диалога (TODO-051)
  const [isInitiateModalOpen, setIsInitiateModalOpen] = useState(false);
  const [inboxScope, setInboxScope] = useState<InboxScope>('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return 360;
    const stored = Number(window.localStorage.getItem('messages.sidebar.width') || 360);
    return Math.min(380, Math.max(340, stored));
  });
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizingRef.current = true;
    startXRef.current = e.clientX;
    startWidthRef.current = sidebarWidth;

    const onMouseMove = (ev: MouseEvent) => {
      if (!isResizingRef.current) return;
      const delta = ev.clientX - startXRef.current;
      const next = Math.min(380, Math.max(340, startWidthRef.current + delta));
      setSidebarWidth(next);
    };
    const onMouseUp = () => {
      isResizingRef.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [sidebarWidth]);

  useEffect(() => {
    window.localStorage.setItem('messages.sidebar.width', String(sidebarWidth));
  }, [sidebarWidth]);
  
  // Дата: 31.01.2026 — Обработка параметра initiate из URL (Итерация 5)
  useEffect(() => {
    const initiate = searchParams.get('initiate');
    if (initiate === 'true') {
      setIsInitiateModalOpen(true);
      // Очищаем параметр из URL
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant } = useTenantScope();
  // Дата: 27.01.2026 - Добавлен debouncedSearch для серверного поиска
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Debounce поиска 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // 15.03.26 - Отдельно держим all-выборку для общих счетчиков и поиска беседы по URL.
  const {
    conversations: sidebarConversations,
    totalConversations: sidebarTotalConversations,
    isLoading: isSidebarLoading,
  } = useMessages(undefined, undefined, tenantId, 'all', debouncedSearch);

  // 15.03.26 - Активная вкладка грузится своим запросом, иначе локальный фильтр мог показать 0 бесед.
  const {
    conversations,
    totalConversations,
    hasMoreConversations,
    fetchMoreConversations,
    isFetchingMore,
    isLoading,
    markAsRead,
  } = useMessages(undefined, undefined, tenantId, channelFilter, debouncedSearch, chatTypeFilter);

  const channelCountQueries = useQueries({
    queries: channelCountFilters.map((filter) => ({
      queryKey: ['conversation-count', tenantId, filter, debouncedSearch],
      queryFn: async () => {
        const response = await messagesApi.getConversations(tenantId, filter, 0, 1, debouncedSearch);
        return response.total;
      },
      enabled: !!tenantId,
      staleTime: 30_000,
    })),
  });
  
  const conversationListRef = useRef<HTMLDivElement>(null);
  
  const handleScroll = useCallback(() => {
    if (!conversationListRef.current || !hasMoreConversations || isFetchingMore) return;
    const { scrollTop, scrollHeight, clientHeight } = conversationListRef.current;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      fetchMoreConversations();
    }
  }, [hasMoreConversations, isFetchingMore, fetchMoreConversations]);
  
  const channelCounts = useMemo(
    () => {
      const counts = {
        all: sidebarTotalConversations || sidebarConversations.length,
        // 15.03.26 - Верхние вкладки показывают server total по каналу, а не count из первых 30 all-бесед.
        waba: channelCountQueries[0]?.data ?? 0,
        regular: channelCountQueries[1]?.data ?? 0,
        telegram_bot: channelCountQueries[2]?.data ?? 0,
        telegram_client: channelCountQueries[3]?.data ?? 0,
        max: channelCountQueries[4]?.data ?? 0,
        max_client: channelCountQueries[5]?.data ?? 0,
        sms: channelCountQueries[6]?.data ?? 0,
      };

      if (channelFilter !== 'all') {
        counts[channelFilter] = totalConversations;
      }

      return counts;
    },
    [channelCountQueries, channelFilter, sidebarConversations.length, sidebarTotalConversations, totalConversations]
  );

  const isDefaultConversationScope = channelFilter === 'all' && chatTypeFilter === 'all' && debouncedSearch.trim() === '';
  const dialogCount = isDefaultConversationScope ? sidebarTotalConversations : totalConversations;
  const visibleConversations = useMemo(
    () => filterLoadedConversations(conversations, inboxScope),
    [conversations, inboxScope],
  );
  const loadedUnreadCount = useMemo(
    () => conversations.filter((item) => item.unread_count > 0).length,
    [conversations],
  );
  const footerLabel = isDefaultConversationScope
    ? `Загружено ${sidebarConversations.length} из ${sidebarTotalConversations} диалогов`
    : `Загружено ${conversations.length} из ${totalConversations} диалогов`;
  
  useEffect(() => {
    if (tenantId) {
      setSelectedPhone(null);
    }
  }, [tenantId]);
  
  // Дата: 31.01.2026 — Обработка phone из URL (переход из /appointments)
  // Дата: 07.02.2026 — FIX: Улучшена логика поиска беседы - проверяем через API если не найдена в списке
  useEffect(() => {
    const phoneFromUrl = searchParams.get('phone');
    if (!phoneFromUrl) {
      checkedPhoneRef.current = null;
      return;
    }
    
    // Если уже проверяли этот телефон, не проверяем повторно
    if (checkedPhoneRef.current === phoneFromUrl) return;
    
    if (tenantId && phoneFromUrl) {
      setSelectedPhone(phoneFromUrl);
    }

    // Ждём загрузки бесед только чтобы подставить имя; чат уже открыт.
    if (isLoading || isSidebarLoading) return;
    
    // Нормализуем телефон для поиска
    const normalizedPhone = phoneFromUrl.replace(/\D/g, '');
    
    // Ищем беседу с этим телефоном в загруженных беседах
    const conv = sidebarConversations.find(c => {
      const convPhone = getConversationThreadKey(c).replace(/\D/g, '');
      return convPhone === normalizedPhone || convPhone.endsWith(normalizedPhone) || normalizedPhone.endsWith(convPhone);
    });
    
    if (conv) {
      // Беседа найдена в списке — выбираем её
      setSelectedPhone(getConversationThreadKey(conv));
      setSelectedPatientName(getConversationDisplayName(conv));
      setNoConversationForPhone(null);
      checkedPhoneRef.current = phoneFromUrl;
      // Очищаем URL параметр
      setSearchParams({}, { replace: true });
      return;
    }
    
    // Беседа не найдена в списке — проверяем через API, есть ли сообщения для этого телефона
    // Это нужно, так как беседа может быть не в первых 30 записях или поиск может не работать правильно
    // 07.03.26 - Для existence-check достаточно 1 сообщения, не тянем весь расширенный хвост истории.
    const checkConversationViaAPI = async () => {
      // Помечаем, что проверяем этот телефон
      checkedPhoneRef.current = phoneFromUrl;
      
      try {
        const response = await messagesApi.getConversationMessages(phoneFromUrl, undefined, tenantId, 1);
        if (response.messages && response.messages.length > 0) {
          // Сообщения есть — открываем чат напрямую
          setSelectedPhone(phoneFromUrl);
          setSelectedPatientName(undefined);
          setNoConversationForPhone(null);
          setSearchParams({}, { replace: true });
        } else {
          // Сообщений нет — показываем сообщение "Чата нет"
          setNoConversationForPhone(phoneFromUrl);
          setSelectedPhone(null);
          setTimeout(() => setSearchParams({}, { replace: true }), 5000);
        }
      } catch (error) {
        // Ошибка API — предполагаем, что чата нет
        setNoConversationForPhone(phoneFromUrl);
        setSelectedPhone(null);
        setTimeout(() => setSearchParams({}, { replace: true }), 5000);
      }
    };
    
    // Проверяем через API только если список бесед уже загружен или загрузка завершена
    if (!isLoading) {
      checkConversationViaAPI();
    }
  }, [searchParams, isLoading, isSidebarLoading, sidebarConversations, setSearchParams, tenantId]);
  
  // Дата: 05.02.2026 - FIX v2: Полностью отключаем автоматический сброс selectedPhone
  // Проблема: При выборе беседы из REG/WABA фильтра она сразу сбрасывалась
  // Причина: Беседа могла иметь messenger = 'whatsapp_waba', но показываться в REG списке через channels
  // Решение: НЕ сбрасываем selectedPhone автоматически. Пользователь сам выбирает беседу.
  // Если нужно сбросить — пользователь переключится на другой фильтр вручную.
  // 
  // УДАЛЕНО: Вся логика автоматического сброса selectedPhone при смене фильтра
  // Беседа остаётся выбранной пока пользователь явно не выберет другую
  
  // Do not full-page-block on the conversations query. Superadmin must see
  // TenantSelector, and ChatWindow must mount from ?phone= so training notes
  // are reachable without waiting on the sidebar.
  
  return (
    <WebSocketProvider
      tenantId={tenantId}
      activePhone={selectedPhone}
      onOpenConversation={(phone) => {
        setSelectedPhone(phone);
        const currentConversation = sidebarConversations.find((c) => getConversationThreadKey(c) === phone);
        setSelectedPatientName(getConversationDisplayName(currentConversation));
      }}
    >
      <MainLayout headerTitle="Входящие" disableMainScroll>
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          {showGuide && (
            <PageGuide
              id="messages-guide"
              title={messagesHelp.pageGuide.title}
              variant="info"
              expandedContent={
                messagesHelp.expandedGuide && (
                  <div className="space-y-1.5">
                    {messagesHelp.expandedGuide.body.map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </div>
                )
              }
            >
              {messagesHelp.pageGuide.body.map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </PageGuide>
          )}

          <div className="flex min-h-0 flex-1 overflow-hidden bg-shell-bg">
          <div
            className="flex min-h-0 flex-shrink-0 flex-col border-r border-shell-border bg-shell-surface"
            style={{ width: sidebarWidth }}
          >
            <div className="space-y-2 border-b border-shell-border px-3 py-2">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-shell-strong">
                    Входящие · {dialogCount || conversations.length}
                  </p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <p className="shrink-0 whitespace-nowrap text-[11px] leading-4 text-shell-muted">диалогов, не сообщений</p>
                    <RealtimeConnectionIndicator />
                  </div>
                </div>
                <button
                  type="button"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-shell-border text-shell-muted hover:bg-shell-hover"
                  title="Справка"
                  onClick={() => setShowGuide((open) => !open)}
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setIsInitiateModalOpen(true)}
                  className="shell-button h-9 px-2.5"
                  data-testid="btn-initiate-conversation"
                  title="Начать диалог"
                >
                  <Plus className="h-4 w-4" />
                </button>
                <HelpTooltip content={messagesHelp.tooltips.initiate_dialog} side="bottom" />
              </div>
              {isSuperAdmin && (
                <TenantSelector value={selectedTenantId || ''} onChange={setSelectedTenant} testId="select-tenant-messages" />
              )}
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-shell-muted" />
                <input
                  type="text"
                  placeholder="Имя, телефон или ID"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="shell-input pl-9 pr-8"
                  data-testid="input-search-conversations"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md hover:bg-shell-hover"
                    title="Очистить"
                  >
                    <X className="h-3.5 w-3.5 text-shell-muted" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className={`h-8 flex-1 rounded-lg text-xs font-semibold ${inboxScope === 'all' ? 'bg-shell-primary-soft text-shell-primary-strong' : 'text-shell-muted hover:bg-shell-hover'}`}
                  onClick={() => setInboxScope('all')}
                >
                  Все
                </button>
                <button
                  type="button"
                  className={`h-8 flex-1 rounded-lg text-xs font-semibold ${inboxScope === 'unread' ? 'bg-shell-primary-soft text-shell-primary-strong' : 'text-shell-muted hover:bg-shell-hover'}`}
                  onClick={() => setInboxScope('unread')}
                  title="Непрочитанные среди уже загруженных диалогов. Серверного фильтра непрочитанных нет."
                >
                  Непрочитанные · {loadedUnreadCount}
                </button>
                <button
                  type="button"
                  className="inline-flex h-8 items-center gap-1 rounded-lg border border-shell-border px-2 text-xs font-semibold text-shell-text hover:bg-shell-hover"
                  onClick={() => setFiltersOpen((open) => !open)}
                >
                  <SlidersHorizontal className="h-3.5 w-3.5" />
                  Фильтры
                </button>
              </div>
              {filtersOpen && (
                <div className="space-y-2 rounded-lg border border-shell-border bg-shell-elevated p-2">
                  <p className="text-[11px] leading-4 text-shell-muted">Числа у каналов — количество диалогов, не сообщений и не непрочитанных.</p>
                  <ChannelFilterTabs value={channelFilter} onChange={setChannelFilter} counts={channelCounts} />
                  <ChannelStatusMonitor tenantId={tenantId} />
                  {(() => {
                    const groupCount = sidebarConversations.filter((c) => c.chat_type === 'group').length;
                    if (groupCount === 0) return null;
                    const personalCount = sidebarConversations.filter((c) => !c.chat_type || c.chat_type === 'personal').length;
                    return (
                      <div className="flex items-center gap-1">
                        {[
                          { key: 'all' as const, label: 'Все типы', count: sidebarConversations.length, icon: null },
                          { key: 'personal' as const, label: 'Личные', count: personalCount, icon: User },
                          { key: 'group' as const, label: 'Групповые', count: groupCount, icon: Users },
                        ].map(({ key, label, count, icon: Icon }) => (
                          <button
                            key={key}
                            onClick={() => setChatTypeFilter(key)}
                            className={`flex h-8 flex-1 items-center justify-center gap-1 rounded-lg text-xs font-semibold ${chatTypeFilter === key ? 'bg-shell-surface text-shell-strong' : 'text-shell-muted'}`}
                            data-testid={`btn-chat-type-${key}`}
                          >
                            {Icon && <Icon className="h-3.5 w-3.5" />}
                            <span>{label}</span>
                            <span>{count}</span>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            <div ref={conversationListRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain" onScroll={handleScroll}>
              {visibleConversations.length === 0 ? (
                <div className="px-4 py-10 text-center">
                  <h3 className="text-sm font-semibold text-shell-strong">
                    {searchQuery || inboxScope === 'unread' ? 'Ничего не найдено' : 'Нет диалогов'}
                  </h3>
                  <p className="mt-1 text-sm text-shell-muted">
                    {inboxScope === 'unread'
                      ? 'Среди загруженных диалогов нет непрочитанных. Прокрутите список в режиме «Все», чтобы добрать историю.'
                      : 'Выберите другой фильтр или начните новый диалог.'}
                  </p>
                </div>
              ) : (
                <ConversationList
                  conversations={visibleConversations}
                  selectedPhone={selectedPhone}
                  onSelect={(phone, patientName) => {
                    setSelectedPhone(phone);
                    setSelectedPatientName(patientName);
                  }}
                  onMarkProcessed={(phone) => {
                    void markAsRead({ phone });
                  }}
                />
              )}
              
              {/* Loading More Indicator */}
              {isFetchingMore && (
                <div className="flex justify-center py-6">
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-shell-elevated">
                    <Spinner size="sm" />
                    <span className="text-xs text-shell-muted">Загрузка...</span>
                  </div>
                </div>
              )}
              
              {/* Count - subtle footer */}
              {sidebarConversations.length > 0 && (
                <div className="sticky bottom-0 border-t border-shell-border bg-shell-surface py-2 text-center text-xs text-shell-muted">
                  <span>{footerLabel}</span>
                </div>
              )}
            </div>
          </div>

          {/* Chat Window */}
          <div
            onMouseDown={handleResizeStart}
            className="w-1.5 flex-shrink-0 cursor-col-resize group relative select-none"
            title="Потяните чтобы изменить ширину"
          >
            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-0.5 bg-shell-border group-hover:bg-shell-primary/50 group-active:bg-shell-primary transition-colors duration-150" />
          </div>
          {/* Chat Window */}
          <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden bg-shell-surface">
            {selectedPhone ? (
              <ChatWindow
                phone={selectedPhone}
                tenantId={tenantId}
                patientName={
                  selectedPatientName ||
                  getConversationDisplayName(
                    sidebarConversations.find((c) => getConversationThreadKey(c) === selectedPhone)
                  )
                }
              />
            ) : noConversationForPhone ? (
              /* Сообщение "Чата нет" */
              <div className="flex-1 flex flex-col items-center justify-center text-center relative overflow-hidden">
                <div className="absolute inset-0"
                  style={{ background: 'linear-gradient(135deg, var(--shell-warning-soft) 0%, var(--shell-elevated) 100%)' }}
                />

                <div className="relative z-10 animate-in fade-in-50 zoom-in-95 duration-500">
                  <div className="relative mb-8">
                    <div className="absolute inset-0 rounded-[32px] blur-2xl opacity-30 animate-pulse"
                      style={{ animationDuration: '3s', background: 'linear-gradient(135deg, var(--shell-warning) 0%, var(--shell-warning) 100%)' }}
                    />
                    <div className="relative w-28 h-28 rounded-[32px] flex items-center justify-center shadow-2xl"
                      style={{ background: 'linear-gradient(135deg, var(--shell-warning) 0%, color-mix(in srgb, var(--shell-warning) 80%, black) 100%)' }}
                    >
                      <AlertCircle className="w-14 h-14 text-white drop-shadow-lg" />
                    </div>
                  </div>

                  <h2 className="text-2xl font-bold text-shell-strong mb-3">
                    Чата с данным пациентом нет
                  </h2>
                  <p className="text-shell-muted max-w-sm leading-relaxed mb-2">
                    Телефон: <span className="font-medium text-shell-text">{noConversationForPhone}</span>
                  </p>
                  <p className="text-shell-muted text-sm max-w-xs opacity-80">
                    Пациент ещё не писал в мессенджеры. Вы можете начать диалог, отправив ему сообщение через рассылку.
                  </p>

                  <button
                    onClick={() => setNoConversationForPhone(null)}
                    className="mt-6 px-4 py-2 text-sm font-medium text-shell-warning hover:bg-shell-warning-soft rounded-xl transition-colors"
                  >
                    Понятно
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center px-6 text-center">
                <div className="max-w-sm">
                  <MessageSquare className="mx-auto mb-3 h-8 w-8 text-shell-muted" />
                  <h2 className="text-base font-semibold text-shell-strong">Диалог не выбран</h2>
                  <p className="mt-1 text-sm leading-5 text-shell-muted">
                    Выберите диалог слева или начните новый. История и отправка останутся в этой панели.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      </MainLayout>

      {/* New Message Toast */}
      <RealtimeNewMessageToast />

      {/* Модалка инициирования диалога - 31.01.2026 TODO-051 */}
      <InitiateConversationModal
        isOpen={isInitiateModalOpen}
        onClose={() => setIsInitiateModalOpen(false)}
        tenantId={tenantId}
        onOpenConversation={(phone) => {
          setSelectedPhone(phone);
          const currentConversation = sidebarConversations.find((c) => getConversationThreadKey(c) === phone);
          setSelectedPatientName(getConversationDisplayName(currentConversation));
        }}
      />
    </WebSocketProvider>
  );
}

function RealtimeConnectionIndicator() {
  const { status } = useWebSocketContext();
  
  return (
    <div className="flex items-center gap-2">
      {status === 'connected' ? (
        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-shell-success" title="Realtime подключён">
          <Wifi className="h-3.5 w-3.5" />
          Связь
        </span>
      ) : status === 'reconnecting' ? (
        <span className="text-[11px] font-medium text-shell-warning">Связь восстанавливается</span>
      ) : (
        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-shell-muted" title="Нет websocket, история обновляется опросом">
          <WifiOff className="h-3.5 w-3.5" />
          Нет связи
        </span>
      )}
    </div>
  );
}

function RealtimeNewMessageToast() {
  const { toast, dismissToast, openToastConversation } = useWebSocketContext();
  return <NewMessageToast toast={toast} onClose={dismissToast} onOpenConversation={openToastConversation} />;
}
